import * as React from 'react';
import { useMarketingSeo, applySeoToDocument } from './MarketingLayout';
import { DEFAULT_SEO } from './api';

/**
 * MarketingPageHead
 * --------------------------------------------------------------
 * Per-page SEO override. Pages drop this into their JSX with an
 * optional title / description / OG / canonical override. It
 * mutates the document <head> imperatively (no helmet dep).
 *
 * On unmount, it restores the SEO defaults from MarketingLayout
 * (which fetches once from `/api/public/site/seo`).
 *
 * Render rules:
 *   - `title`            → "{title} · Worqly" unless `titleTemplate={false}`
 *   - `description`      → meta[name=description], og:description, twitter:description
 *   - `image`            → og:image, twitter:image
 *   - `path`             → canonical (defaults to window.location.pathname)
 *   - `noindex`          → robots = "noindex, nofollow"
 */
export interface MarketingPageHeadProps {
  title?: string;
  description?: string;
  image?: string;
  path?: string;
  noindex?: boolean;
  /** When false, render `title` exactly as provided (no " · Worqly" suffix). */
  titleTemplate?: boolean;
  /**
   * OpenGraph object type. Defaults to "website"; pass "article" on
   * blog posts / docs so crawlers and AI answer engines treat the page
   * as a dated, authored article (enables article:* OG tags below).
   */
  ogType?: 'website' | 'article';
  /** ISO timestamp — emitted as article:published_time when ogType="article". */
  articlePublishedTime?: string;
  /** ISO timestamp — emitted as article:modified_time when ogType="article". */
  articleModifiedTime?: string;
  /** Author name — emitted as article:author when ogType="article". */
  articleAuthor?: string;
}

export function MarketingPageHead(props: MarketingPageHeadProps): null {
  const seo = useMarketingSeo();

  React.useEffect(() => {
    const base = seo ?? DEFAULT_SEO;

    const merged = {
      ...base,
      default_title: composeTitle(props.title, base.default_title, props.titleTemplate),
      default_description: props.description?.trim() || base.default_description,
      default_og_image_url: props.image ?? base.default_og_image_url,
      default_robots: props.noindex ? 'noindex, nofollow' : base.default_robots,
    };

    applySeoToDocument(merged, props.path, {
      ogType: props.ogType,
      articlePublishedTime: props.articlePublishedTime,
      articleModifiedTime: props.articleModifiedTime,
      articleAuthor: props.articleAuthor,
    });

    return () => {
      // Restore the layout defaults on unmount / re-navigate (also clears article:* tags).
      applySeoToDocument(base, undefined);
    };
    // We intentionally re-run when any prop or the loaded seo changes.
  }, [
    seo,
    props.title,
    props.description,
    props.image,
    props.path,
    props.noindex,
    props.titleTemplate,
    props.ogType,
    props.articlePublishedTime,
    props.articleModifiedTime,
    props.articleAuthor,
  ]);

  return null;
}

function composeTitle(
  title: string | undefined,
  fallback: string,
  templateOn: boolean | undefined,
): string {
  const trimmed = title?.trim();
  if (!trimmed) return fallback;
  if (templateOn === false) return trimmed;
  // Avoid double-suffix if caller already included "Worqly".
  if (/worqly/i.test(trimmed)) return trimmed;
  return `${trimmed} · Worqly`;
}
